/**
 * Network adapters for the Buxgalteriya (Accounting) module.
 *
 * Every call validates its payload at the ACL boundary via `typedRequest`
 * (Spring-style `{success, data}` envelope) or `parseWithSchema` (bare bodies).
 * Drift surfaces as a `SchemaParseError` — caught by the global
 * `QueryCache.onError` for query failures, and by callers' `onError` for
 * mutations. Delete endpoints use plain `request` because their success body
 * is `{success: true, message?}` with no `data` to validate.
 *
 * 8 mutation endpoints (createTxn / updateTxn / deleteTxn / attachInvoice /
 * createInvoice / updateInvoice / deleteInvoice / markPaid) are the ones the
 * mutations file consumes; 3 GET endpoints (transactions list, invoices list,
 * dashboard summary) feed the queryOptions factories. The frontend OWNS this
 * contract until the backend ships — keep the URL shape stable.
 */

import { request, type ApiResponse } from '@/shared/api'
import { typedRequest } from '@/shared/api/typed-request'
import {
  AttachInvoicePayload,
  CreateInvoiceSchema,
  CreateTransactionSchema,
  DashboardSummarySchema,
  InvoiceDetailSchema,
  InvoicePageSchema,
  InvoiceSchema,
  PendingPaymentsResponseSchema,
  TransactionPageSchema,
  TransactionSchema,
  UpdateInvoiceSchema,
  UpdateTransactionSchema,
  type CreateInvoice,
  type CreateTransaction,
  type DashboardSummary,
  type Invoice,
  type InvoiceDetail,
  type InvoicePage,
  type PendingPaymentsResponse,
  type Transaction,
  type TransactionPage,
  type UpdateInvoice,
  type UpdateTransaction,
} from './contracts'

const BASE = '/v1/accounting'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function unwrap<T>(res: ApiResponse<T>, fallbackMessage: string): T {
  if (!res.success || res.data === undefined) {
    throw new Error(res.message ?? fallbackMessage)
  }
  return res.data
}

// ─── Transactions ────────────────────────────────────────────────────────────

/**
 * GET /v1/accounting/transactions{paramsSuffix}
 * @param paramsSuffix raw URL query string (must start with `?` or empty)
 *                     e.g. `?page=0&size=20&type=EXPENSE&status=UNPAID`
 */
export async function fetchTransactions(
  paramsSuffix: string,
): Promise<ApiResponse<TransactionPage>> {
  return typedRequest(TransactionPageSchema, {
    url: `${BASE}/transactions${paramsSuffix || ''}`,
  })
}

export async function fetchTransactionById(id: string): Promise<Transaction> {
  const res = await typedRequest(TransactionSchema, {
    url: `${BASE}/transactions/${id}`,
  })
  return unwrap(res, `Transaction ${id} not found`)
}

export async function createTransaction(body: CreateTransaction): Promise<Transaction> {
  const validated = CreateTransactionSchema.parse(body)
  const res = await typedRequest(TransactionSchema, {
    url: `${BASE}/transactions`,
    method: 'POST',
    body: validated,
  })
  return unwrap(res, 'Create transaction failed')
}

export async function updateTransaction(id: string, body: UpdateTransaction): Promise<Transaction> {
  const validated = UpdateTransactionSchema.parse(body)
  const res = await typedRequest(TransactionSchema, {
    url: `${BASE}/transactions/${id}`,
    method: 'PUT',
    body: validated,
  })
  return unwrap(res, `Update transaction ${id} failed`)
}

export async function deleteTransaction(id: string): Promise<void> {
  const res = await request({
    url: `${BASE}/transactions/${id}`,
    method: 'DELETE',
  })
  if (!res.success) throw new Error(res.message ?? `Delete transaction ${id} failed`)
}

/**
 * POST /v1/accounting/transactions/{txnId}/attach-invoice
 * Discriminated payload: CREATE opens a new PENDING invoice over
 * `[txnId, ...extraTxnIds]`; LINK appends `txnId` to an existing PENDING
 * invoice. Returns the affected invoice so the caller can patch both caches.
 */
export async function attachInvoice(
  txnId: string,
  payload: AttachInvoicePayload,
): Promise<Invoice> {
  const validated = AttachInvoicePayload.parse(payload)
  const res = await typedRequest(InvoiceSchema, {
    url: `${BASE}/transactions/${txnId}/attach-invoice`,
    method: 'POST',
    body: validated,
  })
  return unwrap(res, `Attach invoice to transaction ${txnId} failed`)
}

// ─── Invoices ────────────────────────────────────────────────────────────────

/**
 * GET /v1/accounting/invoices{paramsSuffix}
 * @param paramsSuffix raw URL query string (must start with `?` or empty)
 *                     e.g. `?page=0&size=20&status=PENDING&type=EXPENSE`
 */
export async function fetchInvoices(paramsSuffix: string): Promise<ApiResponse<InvoicePage>> {
  return typedRequest(InvoicePageSchema, {
    url: `${BASE}/invoices${paramsSuffix || ''}`,
  })
}

/**
 * GET /v1/accounting/invoices/{id} — returns the invoice with its full
 * attached-transaction rows embedded (`transactions: Transaction[]`), so the
 * detail page can render the attached-txns table + mismatch banner in a
 * single round-trip. Deep-link safe: works on hard refresh / shared URL with
 * a cold list cache. Validated as `InvoiceDetailSchema` (= InvoiceSchema +
 * `transactions`), so a backend that forgets to populate the array fails
 * loud at the ACL boundary.
 */
export async function fetchInvoiceById(id: string): Promise<InvoiceDetail> {
  const res = await typedRequest(InvoiceDetailSchema, {
    url: `${BASE}/invoices/${id}`,
  })
  return unwrap(res, `Invoice ${id} not found`)
}

export async function createInvoice(body: CreateInvoice): Promise<Invoice> {
  const validated = CreateInvoiceSchema.parse(body)
  const res = await typedRequest(InvoiceSchema, {
    url: `${BASE}/invoices`,
    method: 'POST',
    body: validated,
  })
  return unwrap(res, 'Create invoice failed')
}

export async function updateInvoice(id: string, body: UpdateInvoice): Promise<Invoice> {
  const validated = UpdateInvoiceSchema.parse(body)
  const res = await typedRequest(InvoiceSchema, {
    url: `${BASE}/invoices/${id}`,
    method: 'PUT',
    body: validated,
  })
  return unwrap(res, `Update invoice ${id} failed`)
}

export async function deleteInvoice(id: string): Promise<void> {
  const res = await request({
    url: `${BASE}/invoices/${id}`,
    method: 'DELETE',
  })
  if (!res.success) throw new Error(res.message ?? `Delete invoice ${id} failed`)
}

/**
 * PATCH /v1/accounting/invoices/{id}/paid — invoice PENDING → PAID with
 * cascade: every covered txn flips UNPAID/INVOICED → SETTLED server-side.
 * Returns the updated invoice so the caller can refresh its cache; covered
 * transactions are refetched via the global invalidation prefix.
 */
export async function markInvoicePaid(id: string): Promise<Invoice> {
  const res = await typedRequest(InvoiceSchema, {
    url: `${BASE}/invoices/${id}/paid`,
    method: 'PATCH',
  })
  return unwrap(res, `Mark invoice ${id} paid failed`)
}

// ─── Pending Payments aggregate (#9) ─────────────────────────────────────────

/**
 * GET /v1/accounting/pending-payments — single-shot aggregate for the
 * "To'lovga kutilmoqda" page: 3 server-computed summary cards
 * (NON_PAYABLE_EXPENSES / PENDING_INCOME / PAYMENT_OPERATION) +
 * 2 UNPAID-row groups (EXPENSES, INCOME). The 4th "SELECTED" card is
 * composed client-side; the PAYMENT_OPERATION count comes from INVOICED
 * rows that are NOT in the groups, so the page cannot derive that card from
 * the generic transactions list — hence this dedicated endpoint.
 */
export async function fetchPendingPayments(): Promise<ApiResponse<PendingPaymentsResponse>> {
  return typedRequest(PendingPaymentsResponseSchema, {
    url: `${BASE}/pending-payments`,
  })
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

/**
 * GET /v1/accounting/dashboard/summary?from&to — one query feeds every
 * dashboard widget (KPI, daily flow, category donut, account balances,
 * top-5 parties, recent ops, attention banner). Period is ISO date strings.
 */
export async function fetchDashboardSummary(from: string, to: string): Promise<DashboardSummary> {
  const res = await typedRequest(DashboardSummarySchema, {
    url: `${BASE}/dashboard/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  })
  return unwrap(res, 'Dashboard summary fetch failed')
}
